import { SUPABASE_URL } from '../../utils/supabase/url';
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Notification, NotificationPreferences } from '../../types/notifications';
import { DataService } from '../../services/DataService';
import { useAuth } from '../../platform/auth/AuthContext';
import { safeDate } from '../../utils/dateFormat';
import { NotificationService, ToastNotification } from '../../services/NotificationService';
import { projectId } from '../../utils/supabase/info';
import { supabase, getAuthHeaders } from '../../utils/supabase/client';
import { DEFAULT_NOTIFICATION_PREFERENCES, normalizeNotificationPreferences } from '../../notifications/policy';
import { filterInAppNotifications, inAppNotificationQuery } from './filterNotifications';
import { NativeBridge, NATIVE_REFRESH_EVENT } from '../../services/NativeBridge';
import { NetworkGateway } from '../../services/NetworkGateway';
import { createAdaptivePoller, type BackoffPolicy } from '../../polling/adaptivePoller';
import { notificationSnapshotKey } from '../../notifications/snapshotKey';
import { isTauriRuntime, desktopNotify, desktopSetBadge } from '../../platform/tauri/nativeShell';

interface NotificationContextType {
  notifications: Notification[];
  /** Alert history: includes read alerts while showRead is on, otherwise the same as notifications. */
  alertHistory: Notification[];
  showRead: boolean;
  setShowRead: (show: boolean) => void;
  unreadCount: number;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (notificationId: string) => void;
  clearAllNotifications: () => void;
  shortenNotification: (notificationId: string) => Promise<void>;
  preferences: NotificationPreferences;
  updatePreferences: (preferences: NotificationPreferences) => void;
  getNotificationsByType: (type: string) => Notification[];
  getUnreadNotifications: () => Notification[];
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Realtime carries every change, so the poll only catches a socket that died
// without saying so. It stretches while the list stays the same.
const SAFETY_POLL: BackoffPolicy = { baseMs: 5 * 60_000, maxMs: 10 * 60_000 };
// With the channel down, polling is the only source until it rejoins.
const CHANNEL_DOWN_POLL: BackoffPolicy = { baseMs: 30_000, maxMs: 2 * 60_000 };
// A tab flipped away and back inside this window keeps its channel, so it has
// missed nothing and needs no reload. Hidden longer, it disconnects.
const HIDDEN_GRACE_MS = 2 * 60_000;
// A burst of realtime rows (a bulk mark-read, a digest) costs one reload.
const REALTIME_RELOAD_COALESCE_MS = 1_000;
// Preferences and the device badge do not move between reconnects on their own.
const RECONNECT_SIDE_READS_GAP_MS = 15 * 60_000;

/**
 * Realtime postgres_changes rows arrive in snake_case; the rest of the app
 * (and the REST load path) uses the camelCase Notification shape. Merging raw
 * rows into state corrupted display fields (createdAt, actionUrl) for
 * notifications that arrived or were read on another device.
 */
function rowToNotification(row: Record<string, any>): Notification {
  return {
    id: row.id,
    userId: row.user_id ?? row.userId,
    type: row.type,
    priority: row.priority,
    read: row.read,
    title: row.title,
    message: row.message,
    shortMessage: row.short_message ?? row.shortMessage,
    actionUrl: row.action_url ?? row.actionUrl,
    actionLabel: row.action_label ?? row.actionLabel,
    relatedEntityType: row.related_entity_type ?? row.relatedEntityType,
    relatedEntityId: row.related_entity_id ?? row.relatedEntityId,
    relatedId: row.related_entity_id ?? row.relatedEntityId ?? row.relatedId,
    projectId: row.project_id ?? row.projectId,
    projectName: row.project_name ?? row.projectName,
    companyId: row.company_id ?? row.companyId,
    createdAt: row.created_at ?? row.createdAt,
    readAt: row.read_at ?? row.readAt,
    metadata: row.metadata || {},
  } as Notification;
}

// Define which notification types contain financial/privileged data (supervisors CANNOT see these)
const PRIVILEGED_NOTIFICATIONS = [
  'payment_due',           // Financial data
  'payment_received',      // Financial data
  'approval_required',     // Contract decisions
  'approval_approved',     // Contract decisions
  'approval_rejected',     // Contract decisions
  'xero_sync',            // Accounting data
  'deadline_critical',    // May contain contract values
  'deadline_warning',     // May contain contract values
  'user_activity',        // Admin activity
];

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const actorId=auth.user?.id??null;
  const actorRef=useRef(actorId);actorRef.current=actorId;
  const epoch=useRef({actorId,generation:0});
  if(epoch.current.actorId!==actorId)epoch.current={actorId,generation:epoch.current.generation+1};
  const actorGeneration=epoch.current.generation;
  const mounted=useRef(true);
  useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;};},[]);
  const requestGeneration=useRef(0),mutationRevision=useRef(0);
  const pendingMutations=useRef({actorId,count:0});
  if(pendingMutations.current.actorId!==actorId)pendingMutations.current={actorId,count:0};
  const [notificationState,setNotificationState]=useState<{actorId:string|null;actorGeneration:number;rows:Notification[];unreadCount:number|null;policyKey?:string}>({actorId:null,actorGeneration:0,rows:[],unreadCount:null});
  const allNotifications=notificationState.actorId===actorId&&notificationState.actorGeneration===actorGeneration?notificationState.rows:[];
  const localPolicy=useRef({key:'',user:auth.user,company:auth.company,preferences:DEFAULT_NOTIFICATION_PREFERENCES});
  const setAllNotifications=useCallback((change:React.SetStateAction<Notification[]>,countOverride?:number)=>{
    if(!mounted.current||actorRef.current!==actorId||epoch.current.generation!==actorGeneration)return;
    mutationRevision.current++;
    setNotificationState(previous=>{
      if(actorRef.current!==actorId||epoch.current.generation!==actorGeneration)return previous;
      const before=previous.actorId===actorId&&previous.actorGeneration===actorGeneration?previous.rows:[];
      const rows=typeof change==='function'?change(before):change;
      const policy=localPolicy.current;
      const visibleCount=(items:Notification[])=>filterInAppNotifications(items,policy.user,policy.company,policy.preferences).length;
      const delta=visibleCount(rows)-visibleCount(before);
      const count=previous.actorId===actorId&&previous.actorGeneration===actorGeneration&&previous.policyKey===policy.key?previous.unreadCount:null;
      return {actorId,actorGeneration,policyKey:policy.key,rows,unreadCount:countOverride??(count===null?null:Math.max(0,count+delta))};
    });
  },[actorId,actorGeneration]);
  const [preferenceState,setPreferenceState]=useState({actorId,actorGeneration,value:DEFAULT_NOTIFICATION_PREFERENCES});
  const preferences=preferenceState.actorId===actorId&&preferenceState.actorGeneration===actorGeneration?preferenceState.value:DEFAULT_NOTIFICATION_PREFERENCES;
  const setPreferences=useCallback((value:NotificationPreferences)=>{
    if(mounted.current&&actorRef.current===actorId&&epoch.current.generation===actorGeneration)setPreferenceState({actorId,actorGeneration,value});
  },[actorId,actorGeneration]);
  const [showRead,setShowRead]=useState(false);
  const policyQuery=useMemo(()=>inAppNotificationQuery(auth.user,auth.company,preferences),[auth.user?.role,auth.company?.id,preferences]);
  const policyKey=JSON.stringify(policyQuery);
  const policyRef=useRef(policyKey);policyRef.current=policyKey;
  localPolicy.current={key:policyKey,user:auth.user,company:auth.company,preferences};


  // Track already processed mention IDs to avoid duplicate checks - MUST be at top level
  const processedMentionIds = useRef<Set<string>>(new Set());
  
  // Track recent local operations to ignore Realtime echoes
  const recentOperations = useRef<Map<string, { type: 'UPDATE' | 'DELETE', timestamp: number }>>(new Map());

  // Load preferences from backend
  const loadPreferences = useCallback(async () => {
    if (!auth.user) return;

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/make-server-a476273b/users/${auth.user.id}/preferences/notification_preferences`,
        { headers: await getAuthHeaders() },
      );

      if (response.ok) {
        const data = await response.json();
        if (mounted.current&&actorRef.current===auth.user.id&&epoch.current.generation===actorGeneration&&data.value) {
          setPreferences(normalizeNotificationPreferences(data.value));
        }
      }
    } catch (error) {
      // Preferences unavailable — continue with defaults.
    }
  }, [auth.user?.id,actorGeneration]); // account generations also reject ABA preference responses

  // Every published snapshot reports whether it changed what the bell shows,
  // which is what the safety poll backs off on.
  const lastSnapshotKey = useRef('');
  const loadObserver = useRef<((changed: boolean) => void) | null>(null);

  // Load notifications from backend — stable reference via useCallback so the
  // setInterval in the realtime useEffect always holds the current version.
  const loadNotifications = useCallback(async () => {
    // Early return if no user - don't log anything
    if (!auth.user) {
      setAllNotifications([]);
      return;
    }

    // Demo session: never reach the backend. The fixture has no real
    // notifications and we don't want broadcast/admin notifications from
    // other users leaking into the client demo experience.
    if (DataService.isDemoMode()) {
      setAllNotifications([]);
      return;
    }

    const userId=auth.user.id;
    if(actorRef.current!==userId||pendingMutations.current.count)return;
    const generation=++requestGeneration.current;
    const revision=mutationRevision.current;
    const isCurrent=()=>mounted.current&&actorRef.current===userId&&epoch.current.generation===actorGeneration&&policyRef.current===policyKey&&requestGeneration.current===generation&&mutationRevision.current===revision&&!pendingMutations.current.count;
    if(!policyQuery.types.length){
      if(isCurrent())setNotificationState({actorId:userId,actorGeneration,policyKey,rows:[],unreadCount:0});
      return;
    }
    try {
      const snapshot=await NotificationService.getNotificationSnapshot<Notification>(userId,isCurrent,{...policyQuery,unreadOnly:!showRead});
      if(!isCurrent())return;
      if(snapshot.notifications.some(row=>row.userId&&row.userId!==userId))throw new Error('Notification actor could not be confirmed.');
      setNotificationState({actorId:userId,actorGeneration,policyKey,rows:snapshot.notifications,unreadCount:snapshot.unreadCount});
      const key=notificationSnapshotKey(snapshot.notifications,snapshot.unreadCount);
      const changed=key!==lastSnapshotKey.current;
      lastSnapshotKey.current=key;
      loadObserver.current?.(changed);
    } catch {
      // Retain the last complete list/count pair; partial or stale pages never publish.
    }
  }, [auth.user?.id,actorGeneration,policyKey,showRead]); // policy changes also invalidate list/count snapshots

  // Keep a ref to the latest loadNotifications so leaked-interval closures
  // always call the current version, not a stale one.
  const loadNotificationsRef = useRef(loadNotifications);
  useEffect(() => {
    loadNotificationsRef.current = loadNotifications;
  }, [loadNotifications]);

  // Background reloads share one request per account. A reload asked for while
  // one runs means the data moved after that request left, so exactly one more
  // follows. A new account never queues behind the previous account's request.
  // `join` is for the safety poll: any load under way already answers it.
  const reloadFlight = useRef<{ owner: string; running: Promise<void> | null; again: boolean }>({ owner: '', running: null, again: false });
  const reloadNotifications = useCallback((join = false): Promise<void> => {
    const owner = `${actorId}:${actorGeneration}`;
    if (reloadFlight.current.owner !== owner) reloadFlight.current = { owner, running: null, again: false };
    const flight = reloadFlight.current;
    if (flight.running) { if (!join) flight.again = true; return flight.running; }
    flight.running = (async () => {
      do {
        flight.again = false;
        await loadNotificationsRef.current().catch(() => {});
      } while (flight.again && reloadFlight.current === flight);
    })().finally(() => { flight.running = null; });
    return flight.running;
  }, [actorId, actorGeneration]);
  const previousShowRead=useRef(showRead);
  useEffect(()=>{
    if(previousShowRead.current!==showRead){previousShowRead.current=showRead;void reloadNotifications();}
  },[showRead,reloadNotifications]);
  const previousPolicyKey=useRef(policyKey);
  useEffect(()=>{
    if(previousPolicyKey.current!==policyKey){previousPolicyKey.current=policyKey;void loadNotificationsRef.current();}
  },[policyKey]);

  useEffect(() => {
    const handleNativeRefresh = () => {
      reloadNotifications().catch((error) => {
        console.error('Failed to refresh notifications from native request:', error);
      });
    };

    window.addEventListener(NATIVE_REFRESH_EVENT, handleNativeRefresh as EventListener);
    return () => window.removeEventListener(NATIVE_REFRESH_EVENT, handleNativeRefresh as EventListener);
  }, [reloadNotifications]);

  // The Gmail proxy persists the matching notification read before markRead
  // resolves. Reflect that confirmed change immediately in an open bell panel;
  // Realtime and the next fetch remain the cross-device/backstop paths.
  useEffect(() => {
    const handleGmailRead = (event: Event) => {
      const messageId = (event as CustomEvent<{ messageId?: string }>).detail?.messageId;
      if (!messageId||actorRef.current!==actorId) return;
      setAllNotifications((current) => current.map((notification) =>
        notification.type === 'email_received' &&
        notification.metadata?.source === 'gmail' &&
        notification.metadata?.messageId === messageId
          ? { ...notification, read: true, readAt: new Date().toISOString() }
          : notification
      ));
      queueMicrotask(()=>void reloadNotifications());
    };
    window.addEventListener('gmail:message-read', handleGmailRead);
    return () => window.removeEventListener('gmail:message-read', handleGmailRead);
  }, [actorId,setAllNotifications,reloadNotifications]);

  // Real-time sync with Supabase Realtime — pause/resume driven by NetworkGateway.
  useEffect(() => {
    if (!auth.user) return;

    // Demo session: skip the Supabase realtime channel entirely so nothing
    // pushes real-user notifications into the demo.
    if (DataService.isDemoMode()) {
      setAllNotifications([]);
      return;
    }

    const userId = auth.user.id;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let isConnected = false;
    let hasConnectedOnce = false;
    let graceTimer: ReturnType<typeof setTimeout> | null = null;
    let hiddenAt: number | null = null;
    let realtimeTimer: ReturnType<typeof setTimeout> | null = null;
    // Shared by every tab: a reconnect in one tab soon after another's is not a reason to ask again.
    const sideReadsKey = `quantis:notification-side-reads:${userId}`;
    const sideReadsDue = () => {
      try { return Date.now() - (Number(localStorage.getItem(sideReadsKey)) || 0) >= RECONNECT_SIDE_READS_GAP_MS; } catch { return true; }
    };
    const stampSideReads = () => {
      try { localStorage.setItem(sideReadsKey, String(Date.now())); } catch { /* each tab then keeps its own pace */ }
    };

    // Every completed load, whatever asked for it, counts as this poll's run:
    // a realtime-driven reload proves the socket alive as well as a poll does.
    // The poll backs off while what the bell shows stays the same. It never
    // runs early on a return; it only runs once it falls due.
    const safety = createAdaptivePoller({
      policy: SAFETY_POLL,
      minGapMs: Number.POSITIVE_INFINITY,
      run: async () => {
        const before = lastSnapshotKey.current;
        await reloadNotifications(true);
        return lastSnapshotKey.current !== before;
      },
    });
    loadObserver.current = changed => safety.record(changed ? 'changed' : 'unchanged');

    const disconnect = () => {
      if (graceTimer !== null) { clearTimeout(graceTimer); graceTimer = null; }
      if (!isConnected) return;
      isConnected = false;
      if (realtimeTimer !== null) { clearTimeout(realtimeTimer); realtimeTimer = null; }
      // Cleared first, so the CLOSED this removal reports reads as a stale channel's.
      const dying = channel;
      channel = null;
      if (dying) supabase.removeChannel(dying);
    };

    // Bootstrap: load data immediately, independent of NetworkGateway state.
    // navigator.onLine returns false in iOS Simulator PWA contexts and some
    // real iOS PWA installs even when the device has internet connectivity.
    // The HTTP fetch inside loadNotifications handles real-offline failures
    // gracefully, so gating the initial load on NetworkGateway.isActive
    // causes a silent empty-list bug on every affected device.
    loadPreferences();
    safety.record('changed');
    void reloadNotifications();
    NotificationService.syncBadge(userId);
    stampSideReads();

    const connect = () => {
      if (isConnected) return;
      isConnected = true;

      // Refresh only after a real reconnect. On initial mount the bootstrap
      // immediately above has already issued these requests; repeating them
      // here doubled notifications, preferences and badge traffic on every
      // authenticated route. Preferences and the badge only follow a long gap.
      if (hasConnectedOnce) {
        safety.record('changed');
        void reloadNotifications();
        if (sideReadsDue()) {
          stampSideReads();
          loadPreferences();
          NotificationService.syncBadge(userId);
        }
      }
      hasConnectedOnce = true;

      const joined = supabase
        .channel(`notifications:${userId}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
          (payload) => {
            if(actorRef.current!==userId||!mounted.current)return;
            const notificationId = (payload.new as Notification)?.id || (payload.old as Notification)?.id;
            const recentOp = recentOperations.current.get(notificationId);
            if (recentOp && payload.eventType === recentOp.type && Date.now() - recentOp.timestamp < 2000) return;
            if (realtimeTimer === null) {
              realtimeTimer = setTimeout(() => { realtimeTimer = null; void reloadNotifications(); }, REALTIME_RELOAD_COALESCE_MS);
            }
            if (payload.eventType === 'INSERT') {
              const inserted = rowToNotification(payload.new as Record<string, any>);
              // Desktop shell: surface fresh notifications through the OS
              // notification centre when the window is in the background
              // (foreground users already get the in-app toast). Chat
              // messages do not write notifications rows; they notify via
              // MessageNotificationService, so the two paths cannot double-fire.
              if (isTauriRuntime() && !document.hasFocus()) {
                void desktopNotify({ title: inserted.title, body: inserted.message });
              }
              setAllNotifications(prev =>
                prev.some(n => n.id === inserted.id) ? prev : [inserted, ...prev]);
            } else if (payload.eventType === 'UPDATE') {
              const updated = rowToNotification(payload.new as Record<string, any>);
              setAllNotifications(prev => prev.map(n => n.id === updated.id ? updated : n));
            } else if (payload.eventType === 'DELETE') {
              const deleted = payload.old as Notification;
              setAllNotifications(prev => prev.filter(n => n.id !== deleted.id));
            }
          }
        );
      channel = joined;
      let dropped = false;
      joined.subscribe((status) => {
        if (channel !== joined) return;
        if (status === 'SUBSCRIBED') {
          safety.setPolicy(SAFETY_POLL);
          // A rejoin after the socket dropped (sleep, a suspended PWA, a
          // network change) may have missed rows: read once to catch up.
          if (dropped) { dropped = false; safety.record('changed'); void reloadNotifications(); }
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          // supabase-js keeps rejoining; until it is back the poll is the
          // only source, so it runs on the short policy.
          dropped = true;
          safety.setPolicy(CHANNEL_DOWN_POLL);
        }
      });
    };

    // NetworkGateway handles visibilitychange + online/offline in one place.
    const unregister = NetworkGateway.register(`notifications:${userId}`, {
      onActivate: () => {
        // A frozen tab's grace timer can fire late or not at all: hidden past
        // the grace window counts as a reconnect whether or not it fired.
        if (hiddenAt !== null && Date.now() - hiddenAt >= HIDDEN_GRACE_MS) disconnect();
        hiddenAt = null;
        if (graceTimer !== null) { clearTimeout(graceTimer); graceTimer = null; }
        connect();
        safety.resume();
      },
      onDeactivate: () => {
        hiddenAt = Date.now();
        safety.pause();
        // Offline: the socket is already gone. Hidden: hold it for the grace
        // window so a quick tab switch comes back to a live channel.
        if (typeof navigator !== 'undefined' && navigator.onLine === false) disconnect();
        else if (graceTimer === null) graceTimer = setTimeout(disconnect, HIDDEN_GRACE_MS);
      },
    });

    if (NetworkGateway.isActive) { connect(); safety.resume(); }

    return () => {
      if (loadObserver.current) loadObserver.current = null;
      safety.stop();
      disconnect();
      unregister();
    };
  }, [auth.user?.id, reloadNotifications]);

  // Filter notifications based on user role
  const notifications = useMemo(() => {
    return filterInAppNotifications(allNotifications, auth.user, auth.company, preferences);
  }, [allNotifications, auth.user, auth.company, preferences]);

  const alertHistory = useMemo(() => {
    return filterInAppNotifications(allNotifications, auth.user, auth.company, preferences, { includeRead: showRead });
  }, [allNotifications, auth.user, auth.company, preferences, showRead]);

  // The server counts all persisted unread alerts, including those beyond this page.
  // Older deployments without that field retain the previous visible-list fallback.
  const unreadCount = notificationState.actorId===actorId&&notificationState.actorGeneration===actorGeneration&&notificationState.policyKey===policyKey
    ? notificationState.unreadCount??notifications.filter(n=>!n.read).length
    : 0;

  // Keep the app icon badge in sync. Two channels, used per platform:
  //  - Native shell: webkit.messageHandlers.updateBadge → setBadgeCount().
  //  - Installed PWA (iOS 16.4+, desktop Chrome): the web Badging API. The
  //    PWA path was missing entirely, so home-screen badges never showed a
  //    count for web users.
  //
  // Debounced to prevent badge "flashing" on iOS: during a data refresh cycle
  // unreadCount can transition through intermediate values (e.g. old → 0 while
  // loading → new). Without a debounce every transient value renders as a flash.
  const lastSentBadge = useRef<number>(-1);
  const badgeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (badgeDebounceRef.current) clearTimeout(badgeDebounceRef.current);
    badgeDebounceRef.current = setTimeout(() => {
      if (unreadCount !== lastSentBadge.current) {
        lastSentBadge.current = unreadCount;
        NativeBridge.updateBadge('notifications', unreadCount);
        // Desktop shell: macOS dock badge / Windows taskbar badge.
        void desktopSetBadge(unreadCount);
        // Web Badging API — guarded; unsupported browsers simply no-op, and
        // setAppBadge rejects when the document isn't installed/permitted.
        const nav = navigator as Navigator & {
          setAppBadge?: (n?: number) => Promise<void>;
          clearAppBadge?: () => Promise<void>;
        };
        if (unreadCount > 0) {
          nav.setAppBadge?.(unreadCount).catch(() => {});
        } else {
          nav.clearAppBadge?.().catch(() => {});
        }
      }
    }, 300);
    return () => {
      if (badgeDebounceRef.current) clearTimeout(badgeDebounceRef.current);
    };
  }, [unreadCount]);

  const beginMutation=()=>{
    if(!actorId||actorRef.current!==actorId||!mounted.current||epoch.current.generation!==actorGeneration)return null;
    pendingMutations.current.count++;
    requestGeneration.current++;
    return()=>{
      if(actorRef.current!==actorId||epoch.current.generation!==actorGeneration)return;
      pendingMutations.current.count=Math.max(0,pendingMutations.current.count-1);
      if(!pendingMutations.current.count)void loadNotificationsRef.current();
    };
  };

  const markAsRead = async (notificationId: string) => {
    const finish=beginMutation();if(!finish)return;
    // Track this operation to ignore Realtime echo
    recentOperations.current.set(notificationId, { type: 'UPDATE', timestamp: Date.now() });
    
    // Update local state immediately for responsiveness
    setAllNotifications(prev =>
      prev.map(n => (n.id === notificationId ? { ...n, read: true } : n))
    );

    // Update backend
    try {
      await NotificationService.markAsRead(notificationId);
      
      // Clean up tracking after 3 seconds
      setTimeout(() => {
        recentOperations.current.delete(notificationId);
      }, 3000);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Remove from tracking on error
      recentOperations.current.delete(notificationId);
      // Revert local state on error
      await loadNotifications();
      throw error; // Re-throw so caller knows it failed
    } finally {finish();}
  };

  const markAllAsRead = async () => {
    const finish=beginMutation();if(!finish)return;
    // Update local state immediately for responsiveness
    setAllNotifications(prev =>
      prev.map(n => ({ ...n, read: true })),0
    );

    // Update backend
    if (auth.user) {
      try {
        await NotificationService.markAllAsRead(auth.user.id);
      } catch (error) {
        console.error('❌ [NotificationContext] Error marking all as read:', error);
        // Revert local state on error
        await loadNotifications();
      } finally {finish();}
    }
  };

  const deleteNotification = async (notificationId: string) => {
    const finish=beginMutation();if(!finish)return;
    // Track this operation to ignore Realtime echo
    recentOperations.current.set(notificationId, { type: 'DELETE', timestamp: Date.now() });
    
    // Update local state immediately for responsiveness
    setAllNotifications(prev => prev.filter(n => n.id !== notificationId));

    // Delete from backend
    try {
      await NotificationService.deleteNotification(notificationId);
      
      // Clean up tracking after 3 seconds
      setTimeout(() => {
        recentOperations.current.delete(notificationId);
      }, 3000);
    } catch (error) {
      console.error('Error deleting notification:', error);
      // Remove from tracking on error
      recentOperations.current.delete(notificationId);
      // Revert local state on error
      await loadNotifications();
    } finally {finish();}
  };

  const shortenNotification = async (notificationId: string) => {
    if(actorRef.current!==actorId||!actorId)return;
    const updated = await NotificationService.shortenNotification(notificationId);
    // Server response is the source of truth (cached short_message or a
    // freshly-generated one) — merge it into local state rather than
    // optimistically guessing at the shortened text.
    setAllNotifications(prev => prev.map(n => (n.id === notificationId ? updated : n)));
  };

  const clearAllNotifications = async () => {
    const finish=beginMutation();if(!finish)return;
    // Update local state immediately for responsiveness
    setAllNotifications([],0);

    // Clear from backend
    if (auth.user) {
      try {
        await NotificationService.clearAll(auth.user.id);
      } catch (error) {
        console.error('Error clearing notifications:', error);
      } finally {finish();}
    }
  };

  const updatePreferences = async (newPreferences: NotificationPreferences) => {
    if(actorRef.current!==actorId||epoch.current.generation!==actorGeneration)return;
    const normalized = normalizeNotificationPreferences(newPreferences);
    setPreferences(normalized);
    
    if (auth.user) {
      try {
        await fetch(
          `${SUPABASE_URL}/functions/v1/make-server-a476273b/users/${auth.user.id}/preferences/notification_preferences`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(await getAuthHeaders()),
            },
            body: JSON.stringify({ value: normalized }),
          }
        );
      } catch (error) {
        console.error('Failed to save notification preferences:', error);
      }
    }
  };

  const getNotificationsByType = (type: string) => {
    return notifications.filter(n => n.type === type);
  };

  const getUnreadNotifications = () => {
    return notifications.filter(n => !n.read);
  };

  // Monitor activity for @mentions - optimized to reduce redundant checks
  useEffect(() => {
    if (!auth.user) return;

    // Guard flag: prevents the DataService.subscribe callback from triggering
    // another DataService.createNotification call mid-flight, which would cause
    // notifyListeners → subscriber → checkForMentions → createNotification → loop.
    let isMentionCheckRunning = false;

    const checkForMentions = () => {
      if (isMentionCheckRunning) return; // already in-flight, skip
      // Get activities where current user is mentioned
      const mentions = DataService.getUserMentions(auth.user!.id);
      
      mentions.forEach((activity: any) => {
        // Skip if already processed
        if (processedMentionIds.current.has(activity.id)) return;

        // Skip if the activity is older than 24 hours (don't notify for old mentions)
        const activityDate = new Date(activity.created_at);
        const hoursSinceActivity = (Date.now() - activityDate.getTime()) / (1000 * 60 * 60);
        if (hoursSinceActivity > 24) {
          processedMentionIds.current.add(activity.id); // Mark as processed to skip next time
          return;
        }

        // Check if we already have a notification for this mention
        const existingNotification = allNotifications.find(
          n => n.type === 'activity_mention' && n.actionUrl?.split('/').pop() === activity.id
        );
        if (existingNotification) {
          processedMentionIds.current.add(activity.id);
          return;
        }

        // Mark processed BEFORE writing to DataService so a re-entrant call
        // (notifyListeners → subscribe → checkForMentions) can't duplicate it.
        processedMentionIds.current.add(activity.id);

        // Guard flag blocks re-entrance during DataService.createNotification,
        // which triggers notifyListeners → our subscribe callback → checkForMentions.
        isMentionCheckRunning = true;
        const newNotification = DataService.createNotification({
          userId: auth.user!.id,
          companyId: auth.user!.companyId,
          type: 'activity_mention',
          priority: 'medium',
          title: `${activity.created_by_name} mentioned you`,
          message: `In ${activity.related_to_name}: \"${(activity.description || '').substring(0, 80)}${(activity.description || '').length > 80 ? '...' : ''}\"`,
          timestamp: safeDate(activity.created_at) || new Date(),
          read: false,
          actionUrl: `/activity/${activity.id}`,
        });

        isMentionCheckRunning = false;

        // Push to local state only — avoids another DataService write that re-cascades
        setAllNotifications(prev => [newNotification, ...prev]);
      });
    };

    // Check immediately on mount
    checkForMentions();

    // Listen for data changes — the isMentionCheckRunning guard prevents re-entrance
    const unsubscribe = DataService.subscribe(() => {
      checkForMentions();
    });

    return () => {
      unsubscribe();
    };
  }, [auth.user?.id]); // Use stable ID primitive, not the full user object

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        alertHistory,
        showRead,
        setShowRead,
        unreadCount,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAllNotifications,
        shortenNotification,
        preferences,
        updatePreferences,
        getNotificationsByType,
        getUnreadNotifications,
        refreshNotifications: loadNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

/** For surfaces that only refine themselves with notifications and must still render outside the provider. */
export function useOptionalNotifications() {
  return useContext(NotificationContext);
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
